import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import axiosAuth from '../../config/axios';
import { useAuth } from '../../context/AuthContext';
import {
  IconInbox, IconClipboard, IconCheckSquare, IconCalendar, IconUsers,
  IconAlertCircle, IconBarChart, IconBot, IconShield,
} from '../../components/Icons';
import { fecha, hora } from './comun';

// Panel de inicio del orientador: lo que requiere atención hoy, un resumen
// en cifras y el acceso a cada sección del módulo.
const SECCIONES = [
  { Icono: IconInbox,       titulo: 'Remisiones',   desc: 'Remisiones de docentes pendientes de revisión.', ruta: '/bienestar/remisiones' },
  { Icono: IconClipboard,   titulo: 'Casos',        desc: 'Casos abiertos, responsables y línea de tiempo.', ruta: '/bienestar/casos' },
  { Icono: IconCheckSquare, titulo: 'Seguimientos', desc: 'Sesiones, acuerdos y compromisos (dentro de cada caso).', ruta: '/bienestar/casos' },
  { Icono: IconCalendar,    titulo: 'Agenda',       desc: 'Citas con estudiantes, familias y docentes.', ruta: '/bienestar/agenda' },
  { Icono: IconUsers,       titulo: 'Familias',     desc: 'Comunicaciones con acudientes (pestaña Familia de cada caso).', ruta: '/bienestar/casos' },
  { Icono: IconAlertCircle, titulo: 'Señales',      desc: 'Cambios registrados que conviene revisar.', ruta: '/bienestar/senales' },
  { Icono: IconBarChart,    titulo: 'Indicadores',  desc: 'Cifras agregadas del colegio.', ruta: '/bienestar/indicadores' },
  { Icono: IconBot,         titulo: 'Asistente IA', desc: 'Resúmenes y borradores, sin diagnósticos (pestaña Asistente IA de cada caso).', ruta: '/bienestar/casos', ia: true },
];

function Lista({ titulo, vacio, items, render }) {
  return (
    <div style={es.lista}>
      <div style={es.listaTitulo}>{titulo}</div>
      {items.length === 0 ? <div style={es.listaVacia}>{vacio}</div> : items.map(render)}
    </div>
  );
}

export default function BienestarInicio() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [estado, setEstado] = useState(null);
  const [panel, setPanel] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axiosAuth.get('/api/bienestar/estado')
      .then(r => {
        setEstado(r.data.data);
        if (r.data.data?.activo && r.data.data?.es_equipo) {
          axiosAuth.get('/api/bienestar/panel').then(rr => setPanel(rr.data.data)).catch(() => setPanel(null));
        }
      })
      .catch(() => setError('No fue posible consultar el módulo. Intenta de nuevo más tarde.'));
  }, []);

  const primerNombre = usuario?.nombre?.split(' ')[0] || '';
  const insignia = (titulo) => {
    if (!panel) return null;
    if (titulo === 'Remisiones' && panel.remisiones_pendientes) return `${panel.remisiones_pendientes} pendiente${panel.remisiones_pendientes === 1 ? '' : 's'}`;
    if (titulo === 'Agenda' && panel.citas_hoy.length) return `${panel.citas_hoy.length} cita${panel.citas_hoy.length === 1 ? '' : 's'} hoy`;
    if (titulo === 'Señales' && panel.senales_nuevas) return `${panel.senales_nuevas} nueva${panel.senales_nuevas === 1 ? '' : 's'}`;
    return null;
  };
  const vencidos = panel ? panel.compromisos_vencidos.reduce((a, v) => a + v.n, 0) : 0;

  return (
    <div style={es.pagina}>
      <Navbar titulo="Bienestar y Orientación" />
      <div style={es.contenido}>
        <div style={es.encabezado}>
          <div>
            <h2 style={es.titulo}>Hola{primerNombre ? `, ${primerNombre}` : ''}</h2>
            <p style={es.subtitulo}>Panel de orientación escolar</p>
          </div>
          {estado?.nivel && (
            <span style={es.chipNivel}>{estado.nivel === 'lider' ? 'Orientador líder' : 'Orientador'}</span>
          )}
        </div>

        {error && <div style={es.aviso}>{error}</div>}

        {!error && !estado && <p style={es.textoGris}>Cargando...</p>}

        {estado && !estado.activo && (
          <div style={es.aviso}>
            <strong>El módulo todavía no está activo en tu colegio.</strong>
            <br />El administrador lo activa desde “Configuración de bienestar”.
          </div>
        )}

        {estado && estado.activo && !estado.es_equipo && (
          <div style={es.aviso}>
            <strong>Tu cuenta aún no hace parte del equipo de orientación.</strong>
            <br />Pide al administrador que te agregue desde “Configuración de bienestar”.
          </div>
        )}

        {estado?.activo && estado?.es_equipo && (
          <>
            <div style={es.recordatorio}>
              <IconShield size={18} style={{ flexShrink: 0, color: '#5a4fcf' }} />
              <span>
                La información de este módulo es confidencial y cada consulta queda registrada.
                Playfesor apoya la gestión de la orientación; no emite diagnósticos ni reemplaza el criterio profesional.
              </span>
            </div>

            {panel && (
              <>
                <div style={es.resumen}>
                  {[
                    { n: panel.casos_activos, t: estado.nivel === 'lider' ? 'casos activos' : 'casos a tu cargo', ruta: '/bienestar/casos' },
                    { n: panel.remisiones_pendientes, t: 'remisiones pendientes', ruta: '/bienestar/remisiones' },
                    { n: panel.citas_semana, t: 'citas en 7 días', ruta: '/bienestar/agenda' },
                    { n: panel.planes_vigentes, t: 'planes vigentes', ruta: '/bienestar/casos' },
                    { n: vencidos, t: 'compromisos vencidos', ruta: '/bienestar/casos', alerta: vencidos > 0 },
                    { n: panel.senales_nuevas, t: 'señales nuevas', ruta: '/bienestar/senales' },
                  ].map(x => (
                    <button key={x.t} onClick={() => navigate(x.ruta)} style={es.cifra}>
                      <span style={{ ...es.cifraN, ...(x.alerta ? { color: '#c62828' } : {}) }}>{x.n}</span>
                      <span style={es.cifraT}>{x.t}</span>
                    </button>
                  ))}
                </div>

                <h3 style={es.seccion}>Requiere atención hoy</h3>
                <div style={es.atencion}>
                  <Lista titulo="Remisiones urgentes" vacio="Ninguna urgente pendiente." items={panel.remisiones_urgentes}
                    render={r => (
                      <button key={r.id} onClick={() => navigate('/bienestar/remisiones')} style={{ ...es.item, borderLeft: '3px solid #e53935' }}>
                        <strong>{r.estudiante}</strong><span style={es.itemMeta}>desde {fecha(r.creado_en)}</span>
                      </button>
                    )} />
                  <Lista titulo="Tus citas de hoy" vacio="No tienes citas hoy." items={panel.citas_hoy}
                    render={c => (
                      <button key={c.id} onClick={() => navigate(c.caso_id ? `/bienestar/casos/${c.caso_id}` : '/bienestar/agenda')} style={es.item}>
                        <strong>{hora(c.inicio)} · {c.estudiante || c.tipo || 'Cita'}</strong>
                        <span style={es.itemMeta}>{[c.estudiante ? c.tipo : '', c.lugar].filter(Boolean).join(' · ')}</span>
                      </button>
                    )} />
                  <Lista titulo="Compromisos vencidos" vacio="Ninguno vencido." items={panel.compromisos_vencidos}
                    render={v => (
                      <button key={v.caso_id} onClick={() => navigate(`/bienestar/casos/${v.caso_id}`)} style={es.item}>
                        <strong>{v.estudiante}</strong><span style={es.itemMeta}>{v.n} compromiso{v.n === 1 ? '' : 's'} vencido{v.n === 1 ? '' : 's'}</span>
                      </button>
                    )} />
                  <Lista titulo="Sin seguimiento en 30 días" vacio="Todos los casos tienen seguimiento reciente." items={panel.sin_seguimiento}
                    render={c => (
                      <button key={c.caso_id} onClick={() => navigate(`/bienestar/casos/${c.caso_id}`)} style={es.item}>
                        <strong>{c.estudiante}</strong>
                        <span style={es.itemMeta}>{c.ultimo ? `último: ${fecha(c.ultimo)}` : 'sin seguimientos todavía'}</span>
                      </button>
                    )} />
                </div>
              </>
            )}

            <h3 style={es.seccion}>Secciones</h3>
            <div style={es.grid}>
              {SECCIONES.map(({ Icono, titulo, desc, ruta: rutaBase, ia }) => {
                // El asistente solo se abre si el colegio lo activó
                const ruta = ia && !estado.ia_activa ? null : rutaBase;
                const chip = insignia(titulo);
                const contenido = (
                  <>
                    <span style={es.cardIcono}><Icono size={20} /></span>
                    <div style={es.cardTitulo}>{titulo}</div>
                    <div style={es.cardDesc}>{desc}</div>
                    {!ruta ? <span style={es.chipPronto}>{ia ? 'No activado en tu colegio' : 'Próximamente'}</span>
                      : chip ? <span style={es.chipPendientes}>{chip}</span>
                        : <span style={es.chipAbrir}>Abrir →</span>}
                  </>
                );
                return ruta ? (
                  <button key={titulo} onClick={() => navigate(ruta)} style={{ ...es.card, ...es.cardBoton }}>{contenido}</button>
                ) : (
                  <div key={titulo} style={{ ...es.card, opacity: 0.75 }}>{contenido}</div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const es = {
  pagina: { minHeight: '100vh', background: '#f0f2f5' },
  contenido: { padding: '24px', maxWidth: '1100px', margin: '0 auto', width: '100%' },
  encabezado: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' },
  titulo: { fontSize: '22px', fontWeight: 800, color: '#1a1a2e', margin: 0 },
  subtitulo: { fontSize: '13px', color: '#999', margin: '2px 0 0' },
  chipNivel: { background: '#e8eaf6', color: '#3949ab', borderRadius: '999px', padding: '5px 14px', fontSize: '12px', fontWeight: 700 },
  aviso: { background: '#fff8e1', border: '1px solid #ffe082', color: '#8a5a00', borderRadius: '12px', padding: '16px 18px', fontSize: '14px', lineHeight: 1.6 },
  recordatorio: { display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#f3f1ff', border: '1px solid #e0dcff', color: '#4a4380', borderRadius: '12px', padding: '12px 16px', fontSize: '13px', lineHeight: 1.5, marginBottom: '18px' },
  resumen: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: '10px', marginBottom: '8px' },
  cifra: { background: '#fff', border: 'none', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column' },
  cifraN: { fontSize: '22px', fontWeight: 700, color: '#1a1a2e' },
  cifraT: { fontSize: '12px', color: '#888', marginTop: '2px' },
  seccion: { fontSize: '14px', fontWeight: 800, color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px', margin: '22px 0 10px' },
  atencion: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '12px' },
  lista: { background: '#fff', borderRadius: '14px', padding: '14px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '6px' },
  listaTitulo: { fontSize: '13px', fontWeight: 700, color: '#1a1a2e', marginBottom: '4px' },
  listaVacia: { fontSize: '12.5px', color: '#aaa' },
  item: { display: 'flex', flexDirection: 'column', gap: '1px', background: '#fafbff', border: 'none', borderRadius: '8px', padding: '8px 10px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', color: '#1a1a2e' },
  itemMeta: { fontSize: '11.5px', color: '#999' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: '14px' },
  card: { background: '#fff', borderRadius: '14px', padding: '18px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '6px' },
  cardIcono: { width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '4px' },
  cardTitulo: { fontSize: '15px', fontWeight: 700, color: '#1a1a2e' },
  cardDesc: { fontSize: '12.5px', color: '#888', lineHeight: 1.5, flex: 1 },
  cardBoton: { border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
  chipAbrir: { alignSelf: 'flex-start', color: '#667eea', fontSize: '12.5px', fontWeight: 700, marginTop: '4px' },
  chipPendientes: { alignSelf: 'flex-start', background: '#fff3e0', color: '#b45309', borderRadius: '999px', padding: '3px 10px', fontSize: '11.5px', fontWeight: 700, marginTop: '4px' },
  chipPronto: { alignSelf: 'flex-start', background: '#f0f2f5', color: '#999', borderRadius: '999px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, marginTop: '4px' },
  textoGris: { color: '#888', fontSize: '14px' },
};
